/**
 * EmailCampaigns Component
 * Create and send marketing emails to customer segments
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ownerAPI, studioAPI } from '../services/api';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './EmailCampaigns.css';

const STATUS_LABELS = {
  draft: 'Entwurf',
  sending: 'Wird gesendet…',
  sent: 'Gesendet',
  failed: 'Fehlgeschlagen'
};

const STATUS_COLORS = {
  draft: '#6b7d8e',
  sending: '#f59e0b',
  sent: '#22c55e',
  failed: '#ef4444'
};

// Woher die Abmeldung kam
const UNSUB_SOURCE_LABELS = {
  link: 'über Abmelde-Link',
  admin: 'manuell erfasst',
  import: 'übernommen'
};

const CUSTOMER_STATUS_LABELS = {
  Active: 'Aktiv',
  Cancelling: 'Kündigungsfrist',
  Paused: 'Pausiert',
  Cancelled: 'Ex',
  Inactive: 'Inaktiv',
  Prospect: 'Interessent'
};

// Inhaltsbreite der E-Mail in Pixel (siehe email.service.js: max-width 456px).
// Outlook fuer Windows ignoriert CSS max-width, deshalb bekommt jedes
// eingefuegte Bild ein hartes width-Attribut mit dieser Obergrenze.
const EMAIL_CONTENT_WIDTH = 456;

// Adressen aus Freitext ziehen -- gleiche Logik wie parseEmails() im Backend,
// hier nur fuer die Live-Anzeige "N Adressen erkannt".
const extractEmails = (text) => {
  const matches = (text || '').match(/[^\s<>,;"'()[\]]+@[^\s<>,;"'()[\]]+\.[a-z]{2,}/gi) || [];
  return [...new Set(matches.map(e => e.toLowerCase().replace(/[.,;:!?]+$/, '')))];
};

// Anzeigebreite eines Bildes: nie groesser als die Inhaltsbreite, kleine
// Bilder werden nicht hochskaliert.
const getDisplayWidth = (url) => new Promise(resolve => {
  const probe = new Image();
  probe.onload = () => resolve(Math.min(probe.naturalWidth || EMAIL_CONTENT_WIDTH, EMAIL_CONTENT_WIDTH));
  probe.onerror = () => resolve(EMAIL_CONTENT_WIDTH);
  probe.src = url;
});

const EmailCampaigns = ({ showMessage }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [studios, setStudios] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | create | edit | preview
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [recipientPreview, setRecipientPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmSendId, setConfirmSendId] = useState(null);
  const [testEmails, setTestEmails] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [unsubscribes, setUnsubscribes] = useState([]);
  const [unsubInput, setUnsubInput] = useState('');
  const [unsubNote, setUnsubNote] = useState('');
  const [unsubSaving, setUnsubSaving] = useState(false);
  const [unsubSearch, setUnsubSearch] = useState('');
  const quillRef = useRef(null);

  // Form state
  const [form, setForm] = useState({
    subject: '',
    content: '',
    image_url: '',
    segment_studio_ids: [],
    segment_status: [],
    segment_include_leads: false,
    segment_newsletter_only: true
  });

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('image', file);
      try {
        const { data } = await ownerAPI.uploadCampaignImage(formData);
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, 'image', data.url);
        // width-Attribut setzen, damit auch Outlook (Windows) korrekt skaliert
        const width = await getDisplayWidth(data.url);
        editor.formatText(range.index, 1, 'width', String(width), 'user');
        editor.setSelection(range.index + 1);
      } catch (err) {
        showMessage('Fehler beim Hochladen des Bildes', 'error');
      }
    };
  }, [showMessage]);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  }), [imageHandler]);

  const quillFormats = [
    'header', 'bold', 'italic', 'underline',
    'color', 'background', 'list', 'align', 'link', 'image',
    'width' // Bildbreite (Outlook-Fallback), sonst wuerde Quill das Attribut verwerfen
  ];

  const fetchCampaigns = useCallback(async () => {
    try {
      const { data } = await ownerAPI.getCampaigns();
      setCampaigns(data);
    } catch (err) {
      showMessage('Fehler beim Laden der Kampagnen', 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  const fetchFilters = useCallback(async () => {
    try {
      const [studioRes, customerRes] = await Promise.all([
        studioAPI.getAll(),
        ownerAPI.getCustomers({})
      ]);
      setStudios(studioRes.data || []);
      // Extract unique status values from customers
      const uniqueStatuses = [...new Set((customerRes.data || []).map(c => c.status).filter(Boolean))].sort();
      setStatuses(uniqueStatuses);
    } catch (err) {
      console.error('Failed to load filters:', err);
    }
  }, []);

  const fetchUnsubscribes = useCallback(async () => {
    try {
      const { data } = await ownerAPI.getUnsubscribes();
      setUnsubscribes(data || []);
    } catch (err) {
      console.error('Failed to load unsubscribes:', err);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
    fetchFilters();
    fetchUnsubscribes();
  }, [fetchCampaigns, fetchFilters, fetchUnsubscribes]);

  const resetForm = () => {
    setForm({
      subject: '',
      content: '',
      image_url: '',
      segment_studio_ids: [],
      segment_status: [],
      segment_include_leads: false,
      segment_newsletter_only: true
    });
    setEditingCampaign(null);
    setRecipientPreview(null);
  };

  const handleCreate = () => {
    resetForm();
    setView('create');
  };

  // Prefill the form from an existing campaign (used by edit + duplicate)
  const fillForm = (campaign) => {
    setForm({
      subject: campaign.subject,
      content: campaign.content,
      image_url: campaign.image_url || '',
      segment_studio_ids: campaign.segment_studio_ids || [],
      segment_status: (campaign.segment_status || []).filter(s => s !== '_leads'),
      segment_include_leads: (campaign.segment_status || []).includes('_leads') || campaign.segment_include_leads || false,
      segment_newsletter_only: campaign.segment_newsletter_only !== false
    });
    setRecipientPreview(null);
  };

  const handleEdit = (campaign) => {
    fillForm(campaign);
    setEditingCampaign(campaign);
    setView('edit');
  };

  // Duplicate: prefill from a sent/failed campaign but save as a NEW draft.
  // editingCampaign stays null so handleSave calls createCampaign instead of
  // updateCampaign (the backend rejects updates on anything but drafts).
  const handleDuplicate = (campaign) => {
    fillForm(campaign);
    setEditingCampaign(null);
    setView('duplicate');
  };

  const handleSave = async () => {
    if (!form.subject.trim() || !form.content.trim()) {
      showMessage('Betreff und Inhalt sind erforderlich', 'error');
      return;
    }

    try {
      if (editingCampaign) {
        await ownerAPI.updateCampaign(editingCampaign.id, form);
        showMessage('Kampagne aktualisiert');
      } else {
        await ownerAPI.createCampaign(form);
        showMessage('Kampagne erstellt');
      }
      setView('list');
      resetForm();
      fetchCampaigns();
    } catch (err) {
      showMessage(err.response?.data?.error || 'Fehler beim Speichern', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Kampagne wirklich löschen?')) return;
    try {
      await ownerAPI.deleteCampaign(id);
      showMessage('Kampagne gelöscht');
      fetchCampaigns();
    } catch (err) {
      showMessage(err.response?.data?.error || 'Fehler beim Löschen', 'error');
    }
  };

  const handlePreviewRecipients = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await ownerAPI.previewRecipients({
        segment_studio_ids: form.segment_studio_ids,
        segment_status: form.segment_status,
        segment_include_leads: form.segment_include_leads,
        segment_newsletter_only: form.segment_newsletter_only
      });
      setRecipientPreview(data);
    } catch (err) {
      showMessage('Fehler beim Laden der Empfänger', 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async (id) => {
    setSending(true);
    try {
      const { data } = await ownerAPI.sendCampaign(id);
      showMessage(data.message);
      setConfirmSendId(null);
      // Refresh after a short delay to pick up the status change
      setTimeout(() => fetchCampaigns(), 2000);
    } catch (err) {
      showMessage(err.response?.data?.error || 'Fehler beim Senden', 'error');
    } finally {
      setSending(false);
    }
  };

  const toggleStudioFilter = (studioId) => {
    setForm(prev => ({
      ...prev,
      segment_studio_ids: prev.segment_studio_ids.includes(studioId)
        ? prev.segment_studio_ids.filter(id => id !== studioId)
        : [...prev.segment_studio_ids, studioId]
    }));
    setRecipientPreview(null);
  };

  const toggleStatusFilter = (status) => {
    setForm(prev => {
      const current = prev.segment_status;
      const next = current.includes(status)
        ? current.filter(s => s !== status)
        : [...current, status];
      return { ...prev, segment_status: next };
    });
    setRecipientPreview(null);
  };

  const handleSendTest = async () => {
    const emails = testEmails.split(/[,;\s]+/).map(e => e.trim()).filter(Boolean);
    if (emails.length === 0) {
      showMessage('Bitte mindestens eine E-Mail-Adresse eingeben', 'error');
      return;
    }
    if (!form.subject.trim() || !form.content.trim()) {
      showMessage('Betreff und Inhalt sind erforderlich', 'error');
      return;
    }
    setSendingTest(true);
    try {
      const { data } = await ownerAPI.sendTestEmail({
        subject: form.subject,
        content: form.content,
        image_url: form.image_url || null,
        test_emails: emails
      });
      showMessage(data.message);
    } catch (err) {
      showMessage(err.response?.data?.error || 'Fehler beim Senden der Test-E-Mail', 'error');
    } finally {
      setSendingTest(false);
    }
  };

  const handleAddUnsubscribes = async () => {
    const parsed = extractEmails(unsubInput);
    if (parsed.length === 0) {
      showMessage('Keine gültige E-Mail-Adresse erkannt', 'error');
      return;
    }
    setUnsubSaving(true);
    try {
      const { data } = await ownerAPI.addUnsubscribes({ emails: unsubInput, note: unsubNote });
      showMessage(data.message);
      setUnsubInput('');
      setUnsubNote('');
      fetchUnsubscribes();
      // Empfaengervorschau ist jetzt veraltet
      setRecipientPreview(null);
    } catch (err) {
      showMessage(err.response?.data?.error || 'Fehler beim Speichern der Abmeldung', 'error');
    } finally {
      setUnsubSaving(false);
    }
  };

  const handleDeleteUnsubscribe = async (entry) => {
    if (!window.confirm(`${entry.email} wieder für Kampagnen freigeben?`)) return;
    try {
      const { data } = await ownerAPI.deleteUnsubscribe(entry.id);
      showMessage(data.message);
      fetchUnsubscribes();
      setRecipientPreview(null);
    } catch (err) {
      showMessage(err.response?.data?.error || 'Fehler beim Entfernen', 'error');
    }
  };

  // ---- Render: Campaign List ----
  const renderList = () => (
    <div className="campaigns-container">
      <div className="campaigns-header">
        <div>
          <h2>E-Mail Kampagnen</h2>
          <p className="campaigns-subtitle">Marketing-E-Mails an Kundensegmente senden</p>
        </div>
        <div className="campaigns-header-actions">
          <button className="campaign-btn" onClick={() => setView('unsubscribes')}>
            Abmeldungen{unsubscribes.length > 0 ? ` (${unsubscribes.length})` : ''}
          </button>
          <button className="campaign-btn campaign-btn-primary" onClick={handleCreate}>
            + Neue Kampagne
          </button>
        </div>
      </div>

      {loading ? (
        <div className="campaigns-loading">Laden…</div>
      ) : campaigns.length === 0 ? (
        <div className="campaigns-empty">
          <div className="campaigns-empty-icon">✉</div>
          <p>Noch keine Kampagnen erstellt</p>
          <button className="campaign-btn campaign-btn-primary" onClick={handleCreate}>
            Erste Kampagne erstellen
          </button>
        </div>
      ) : (
        <div className="campaigns-list">
          {campaigns.map(c => (
            <div key={c.id} className="campaign-card">
              <div className="campaign-card-header">
                <div className="campaign-card-title">{c.subject}</div>
                <span
                  className="campaign-status-badge"
                  style={{ background: `${STATUS_COLORS[c.status]}18`, color: STATUS_COLORS[c.status] }}
                >
                  {STATUS_LABELS[c.status]}
                </span>
              </div>

              <div className="campaign-card-meta">
                <span>
                  {format(new Date(c.created_at), 'dd. MMM yyyy, HH:mm', { locale: de })}
                </span>
                {c.sent_at && (
                  <span>
                    Gesendet: {format(new Date(c.sent_at), 'dd. MMM yyyy, HH:mm', { locale: de })}
                  </span>
                )}
                {c.status === 'sent' && (
                  <span className="campaign-stats-inline">
                    {c.sent_count} zugestellt{c.failed_count > 0 ? ` · ${c.failed_count} fehlgeschlagen` : ''}
                  </span>
                )}
              </div>

              <div className="campaign-card-preview">
                {c.content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/\s+/g, ' ').trim().substring(0, 120)}…
              </div>

              <div className="campaign-card-actions">
                {c.status === 'draft' && (
                  <>
                    <button className="campaign-btn campaign-btn-sm" onClick={() => handleEdit(c)}>
                      Bearbeiten
                    </button>
                    {confirmSendId === c.id ? (
                      <span className="campaign-confirm-send">
                        <span>Wirklich senden?</span>
                        <button
                          className="campaign-btn campaign-btn-sm campaign-btn-primary"
                          onClick={() => handleSend(c.id)}
                          disabled={sending}
                        >
                          {sending ? 'Sende…' : 'Ja, senden'}
                        </button>
                        <button
                          className="campaign-btn campaign-btn-sm"
                          onClick={() => setConfirmSendId(null)}
                        >
                          Abbrechen
                        </button>
                      </span>
                    ) : (
                      <button
                        className="campaign-btn campaign-btn-sm campaign-btn-send"
                        onClick={() => setConfirmSendId(c.id)}
                      >
                        Senden
                      </button>
                    )}
                    <button
                      className="campaign-btn campaign-btn-sm campaign-btn-danger"
                      onClick={() => handleDelete(c.id)}
                    >
                      Löschen
                    </button>
                  </>
                )}
                {c.status === 'sending' && (
                  <span className="campaign-sending-indicator">Versand läuft…</span>
                )}
                {(c.status === 'sent' || c.status === 'failed') && (
                  <button className="campaign-btn campaign-btn-sm" onClick={() => handleDuplicate(c)}>
                    Duplizieren
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ---- Render: Create/Edit Form ----
  const renderForm = () => (
    <div className="campaigns-container">
      <div className="campaigns-header">
        <div>
          <h2>{editingCampaign ? 'Kampagne bearbeiten' : view === 'duplicate' ? 'Kampagne duplizieren' : 'Neue Kampagne'}</h2>
          <p className="campaigns-subtitle">E-Mail erstellen und Zielgruppe auswählen</p>
        </div>
        <button className="campaign-btn" onClick={() => { setView('list'); resetForm(); }}>
          Zurück zur Liste
        </button>
      </div>

      <div className="campaign-form">
        {/* Email Content Section */}
        <div className="campaign-form-section">
          <h3>E-Mail Inhalt</h3>

          <div className="campaign-field">
            <label>Betreff *</label>
            <input
              type="text"
              value={form.subject}
              onChange={e => setForm(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="z.B. Herbstaktion – 20% auf alle Trainings!"
              className="campaign-input"
            />
          </div>

          <div className="campaign-field">
            <label>Bild-URL (optional)</label>
            <input
              type="url"
              value={form.image_url}
              onChange={e => setForm(prev => ({ ...prev, image_url: e.target.value }))}
              placeholder="https://example.com/bild.jpg"
              className="campaign-input"
            />
            {form.image_url && (
              <div className="campaign-image-preview">
                <img src={form.image_url} alt="Vorschau" onError={e => e.target.style.display = 'none'} />
              </div>
            )}
          </div>

          <div className="campaign-field">
            <label>Inhalt *</label>
            <div className="campaign-editor-wrapper">
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={form.content}
                onChange={value => setForm(prev => ({ ...prev, content: value }))}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Schreibe deine E-Mail hier…"
              />
            </div>
            <span className="campaign-field-hint">
              Platzhalter: <code>{'{{vorname}}'}</code> <code>{'{{nachname}}'}</code> <code>{'{{email}}'}</code> — werden pro Empfänger automatisch ersetzt.
            </span>
          </div>
        </div>

        {/* Segment Section */}
        <div className="campaign-form-section">
          <h3>Zielgruppe</h3>

          {/* Status Filter */}
          <div className="campaign-field">
            <label>Kundenstatus {form.segment_status.length === 0 && <span className="campaign-field-hint-inline">(alle)</span>}</label>
            <div className="campaign-chips">
              {statuses.map(status => (
                <button
                  key={status}
                  className={`campaign-chip ${form.segment_status.includes(status) ? 'active' : ''}`}
                  onClick={() => toggleStatusFilter(status)}
                >
                  {CUSTOMER_STATUS_LABELS[status] || status}
                </button>
              ))}
            </div>
          </div>

          {/* Leads Toggle */}
          <div className="campaign-field">
            <label>Kontaktanfragen (Leads)</label>
            <div className="campaign-chips">
              <button
                className={`campaign-chip ${form.segment_include_leads ? 'active' : ''}`}
                onClick={() => { setForm(prev => ({ ...prev, segment_include_leads: !prev.segment_include_leads })); setRecipientPreview(null); }}
              >
                Leads einschließen
              </button>
            </div>
            <span className="campaign-field-hint">
              Anfragen vom Kontaktformular. Bereits konvertierte Leads und Leads, die schon als Kunde existieren, werden automatisch ausgeschlossen.
            </span>
          </div>

          {/* Studio Filter */}
          {studios.length > 0 && (
            <div className="campaign-field">
              <label>Studios {form.segment_studio_ids.length === 0 && <span className="campaign-field-hint-inline">(alle Studios)</span>}</label>
              <div className="campaign-chips">
                {studios.map(s => (
                  <button
                    key={s.id}
                    className={`campaign-chip ${form.segment_studio_ids.includes(s.id) ? 'active' : ''}`}
                    onClick={() => toggleStudioFilter(s.id)}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Newsletter Opt-out */}
          <div className="campaign-field">
            <label className="campaign-checkbox-label">
              <input
                type="checkbox"
                checked={form.segment_newsletter_only}
                onChange={e => { setForm(prev => ({ ...prev, segment_newsletter_only: e.target.checked })); setRecipientPreview(null); }}
              />
              Nur Kunden ohne Newsletter-Abmeldung
            </label>
            <span className="campaign-field-hint">
              {unsubscribes.length > 0 ? (
                <>
                  {unsubscribes.length} abgemeldete {unsubscribes.length === 1 ? 'Adresse wird' : 'Adressen werden'} unabhängig
                  von dieser Einstellung immer ausgeschlossen.{' '}
                  <button type="button" className="campaign-link-btn" onClick={() => setView('unsubscribes')}>
                    Abmeldungen verwalten
                  </button>
                </>
              ) : (
                <>
                  Abgemeldete Adressen werden immer ausgeschlossen.{' '}
                  <button type="button" className="campaign-link-btn" onClick={() => setView('unsubscribes')}>
                    Abmeldungen verwalten
                  </button>
                </>
              )}
            </span>
          </div>

          {/* Recipient Preview */}
          <div className="campaign-recipients-section">
            <button
              className="campaign-btn campaign-btn-outline"
              onClick={handlePreviewRecipients}
              disabled={previewLoading}
            >
              {previewLoading ? 'Laden…' : 'Empfänger anzeigen'}
            </button>

            {recipientPreview && (
              <div className="campaign-recipients-preview">
                <div className="campaign-recipients-count">
                  <strong>{recipientPreview.count}</strong> Empfänger
                  {form.content.includes('{{vorname}}') && (
                    <span className="campaign-recipients-hint"> — Personalisierung aktiv</span>
                  )}
                </div>
                {recipientPreview.count > 0 && (
                  <div className="campaign-recipients-list">
                    {recipientPreview.recipients.slice(0, 30).map(r => {
                      const personalized = form.subject
                        .replace(/\{\{vorname\}\}/g, r.name || '')
                        .replace(/\{\{nachname\}\}/g, r.last_name || '')
                        .replace(/\{\{email\}\}/g, r.email || '');
                      return (
                        <div key={r.id} className="campaign-recipient-row">
                          <div className="campaign-recipient-info">
                            <span className="campaign-recipient-name">
                              {r.name} {r.last_name}
                              {r._isLead && <span className="campaign-recipient-lead-badge">Lead</span>}
                            </span>
                            <span className="campaign-recipient-email">{r.email}</span>
                          </div>
                          {form.content.includes('{{vorname}}') && (
                            <div className="campaign-recipient-personalized">
                              Betreff: {personalized}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {recipientPreview.count > 30 && (
                      <div className="campaign-recipients-more">
                        … und {recipientPreview.count - 30} weitere
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Email Preview */}
        {form.subject && form.content && form.content.replace(/<[^>]*>/g, '').trim() && (() => {
          const previewRecipient = recipientPreview?.recipients?.[0];
          const previewName = previewRecipient?.name || 'Hey';
          const previewLastName = previewRecipient?.last_name || '';
          const previewEmail = previewRecipient?.email || 'max@beispiel.at';
          const personalizeText = (text) => text
            .replace(/\{\{vorname\}\}/g, previewName)
            .replace(/\{\{nachname\}\}/g, previewLastName)
            .replace(/\{\{email\}\}/g, previewEmail);
          return (
          <div className="campaign-form-section">
            <h3>Vorschau {previewRecipient ? `(${previewName} ${previewLastName})` : '(Beispiel)'}</h3>
            <div className="campaign-email-preview">
              <div className="campaign-email-preview-header">
                <strong>Betreff:</strong> {personalizeText(form.subject)}
              </div>
              <div className="campaign-email-preview-body">
                <div className="campaign-email-preview-brand">
                  <img src="https://cdn.prod.website-files.com/6934931d2112f48107f737f0/6934a5c956d45fd81bd0737d_FRANZ_LOGO_CLEAN_RZ.svg" alt="Der Starke Franz" className="campaign-email-preview-logo" style={{ maxWidth: '140px', height: 'auto' }} />
                </div>
                <div className="campaign-email-preview-card">
                  {form.image_url && (
                    <img src={form.image_url} alt="" className="campaign-email-preview-image" />
                  )}
                  <div
                    className="campaign-email-preview-content"
                    dangerouslySetInnerHTML={{ __html: personalizeText(form.content) }}
                  />
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        {/* Test Email */}
        {form.subject && form.content && (
          <div className="campaign-form-section">
            <h3>Test-E-Mail senden</h3>
            <div className="campaign-field">
              <label>E-Mail-Adressen (kommagetrennt)</label>
              <div className="campaign-test-row">
                <input
                  type="text"
                  value={testEmails}
                  onChange={e => setTestEmails(e.target.value)}
                  placeholder="deine@email.at, partner@email.at"
                  className="campaign-input"
                />
                <button
                  className="campaign-btn campaign-btn-outline"
                  onClick={handleSendTest}
                  disabled={sendingTest || !testEmails.trim()}
                >
                  {sendingTest ? 'Sende…' : 'Test senden'}
                </button>
              </div>
              <span className="campaign-field-hint">
                Betreff wird mit [TEST] markiert. Die E-Mail wird sofort gesendet.
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="campaign-form-actions">
          <button className="campaign-btn" onClick={() => { setView('list'); resetForm(); }}>
            Abbrechen
          </button>
          <button className="campaign-btn campaign-btn-primary" onClick={handleSave}>
            {editingCampaign ? 'Aktualisieren' : 'Als Entwurf speichern'}
          </button>
        </div>
      </div>
    </div>
  );

  // ---- Render: Newsletter-Abmeldungen ----
  const renderUnsubscribes = () => {
    const parsed = extractEmails(unsubInput);
    const term = unsubSearch.trim().toLowerCase();
    const visible = term
      ? unsubscribes.filter(u => u.email.includes(term) || (u.note || '').toLowerCase().includes(term))
      : unsubscribes;

    return (
      <div className="campaigns-container">
        <div className="campaigns-header">
          <div>
            <h2>Newsletter-Abmeldungen</h2>
            <p className="campaigns-subtitle">
              Diese Adressen erhalten keine Kampagnen mehr – gilt für Kunden und Leads.
              Abmeldungen über den Link in der E-Mail landen automatisch hier.
            </p>
          </div>
          <button className="campaign-btn" onClick={() => { setView('list'); setUnsubSearch(''); }}>
            Zurück zur Liste
          </button>
        </div>

        <div className="campaign-form">
          <div className="campaign-form-section">
            <h3>Adressen abmelden</h3>

            <div className="campaign-field">
              <label>E-Mail-Adressen</label>
              <textarea
                className="campaign-textarea"
                rows={4}
                value={unsubInput}
                onChange={e => setUnsubInput(e.target.value)}
                placeholder={'anna@beispiel.at, ben@beispiel.at\n<carla@beispiel.at>'}
              />
              <span className="campaign-field-hint">
                Format ist egal – Kommas, Semikolons, Zeilenumbrüche und spitze Klammern werden erkannt.
                Du kannst die Abmelde-Mails direkt hier einfügen.
                {parsed.length > 0 && (
                  <> <strong>{parsed.length} {parsed.length === 1 ? 'Adresse' : 'Adressen'} erkannt:</strong> {parsed.join(', ')}</>
                )}
              </span>
            </div>

            <div className="campaign-field">
              <label>Notiz (optional)</label>
              <input
                type="text"
                className="campaign-input"
                value={unsubNote}
                onChange={e => setUnsubNote(e.target.value)}
                placeholder="z.B. Abmeldung per Mail-Antwort"
              />
            </div>

            <button
              className="campaign-btn campaign-btn-primary"
              onClick={handleAddUnsubscribes}
              disabled={unsubSaving || parsed.length === 0}
            >
              {unsubSaving
                ? 'Speichere…'
                : parsed.length > 1 ? `${parsed.length} Adressen abmelden` : 'Abmelden'}
            </button>
          </div>

          <div className="campaign-form-section">
            <h3>Abgemeldet ({unsubscribes.length})</h3>

            {unsubscribes.length === 0 ? (
              <p className="campaign-field-hint">Noch keine Abmeldungen erfasst.</p>
            ) : (
              <>
                {unsubscribes.length > 8 && (
                  <div className="campaign-field">
                    <input
                      type="search"
                      className="campaign-input"
                      value={unsubSearch}
                      onChange={e => setUnsubSearch(e.target.value)}
                      placeholder="Adresse suchen…"
                    />
                  </div>
                )}

                <div className="campaign-recipients-list">
                  {visible.map(u => (
                    <div key={u.id} className="campaign-unsub-row">
                      <div className="campaign-unsub-info">
                        <span className="campaign-recipient-name">{u.email}</span>
                        <span className="campaign-unsub-meta">
                          {format(new Date(u.created_at), 'dd. MMM yyyy', { locale: de })}
                          {' · '}{UNSUB_SOURCE_LABELS[u.source] || u.source}
                          {u.note ? ` · ${u.note}` : ''}
                        </span>
                      </div>
                      <button
                        className="campaign-btn campaign-btn-sm"
                        onClick={() => handleDeleteUnsubscribe(u)}
                      >
                        Wieder anmelden
                      </button>
                    </div>
                  ))}
                  {visible.length === 0 && (
                    <div className="campaign-recipient-row">Keine Treffer für „{unsubSearch}“</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (view === 'unsubscribes') return renderUnsubscribes();
  return view === 'list' ? renderList() : renderForm();
};

export default EmailCampaigns;
